import { CircleCheckBig, Loader2 } from 'lucide-react';
import { type ReactElement, useCallback, useMemo } from 'react';
import { Column, Getter, Row } from '@tanstack/react-table';

import { cn } from '@crossnokaye/ux-common';

import { TooltipButton } from '@/components/molecules/tooltip-button';
import {
  ActivationsDataTableColumnMeta,
  AlarmTableData,
} from '@/features/alarms/alarm-tables/types';
import { useAcknowledgeAlarmActivations } from '@/hooks/query-hooks/alarm-activations';
import {
  DeviceTypeDetectionVariant,
  useDeviceTypeDetection,
} from '@/ux-common/hooks/use-device-type-detection/use-device-type-detection';

export const ActionAcknowledgeCell = <TData extends AlarmTableData, TValue>({
  column,
  getValue,
  row,
}: {
  column: Column<TData, TValue>;
  getValue: Getter<TValue>;
  row: Row<TData>;
}): ReactElement => {
  const { isMobile } = useDeviceTypeDetection({
    variant: DeviceTypeDetectionVariant.DeviceOnly,
  });
  const meta = column.columnDef.meta as ActivationsDataTableColumnMeta<TData, TValue>;
  const value = getValue() as string;

  const mutation = useAcknowledgeAlarmActivations({
    orgId: meta?.organizationId,
    agentId: meta?.agentId,
  });

  const handleClick = useCallback(() => {
    mutation?.mutate([row.original.activationId]);
  }, [mutation, row.original.activationId]);

  const isLoading = useMemo(() => {
    return mutation?.isPending || meta.isLoading;
  }, [mutation?.isPending, meta.isLoading]);

  return (
    <div className={'flex justify-center w-full p-2'}>
      {isMobile ? (
        <TooltipButton
          disabled={meta.disabled}
          tooltipContent={meta?.tooltipText}
          onClick={handleClick}
          className="btn btn-tier2"
        >
          {isLoading ? (
            <Loader2
              className={cn(
                'glyph glyph-sm text-foreground animate-spin',
                meta.disabled && 'text-muted-foreground'
              )}
              aria-label="Loading"
            />
          ) : (
            <CircleCheckBig
              className={cn(
                'glyph glyph-sm text-foreground',
                meta.disabled && 'text-muted-foreground'
              )}
              aria-hidden="true"
            />
          )}
        </TooltipButton>
      ) : (
        <TooltipButton
          disabled={meta.disabled}
          tooltipContent={meta?.tooltipText}
          onClick={handleClick}
          className="btn btn-tier2"
        >
          {isLoading ? (
            <Loader2 className="glyph glyph-sm text-foreground animate-spin" aria-label="Loading" />
          ) : (
            value
          )}
        </TooltipButton>
      )}
    </div>
  );
};
